<template>
    <component-loader v-if="loading && !filteredTableData" />
    <generic-table
        v-else-if="timezoneAdjustedTableData && liveTableOptions"
        ref="genericTableRef"
        :data="timezoneAdjustedTableData"
        :is-loading="loading"
        :dataset-key="storeTableKey"
        :backend-filters="backendFilters"
        :hardcoded-table-filters="props.hardcodedTableFilters"
        :hide-top-filters="props.hideTopFilters"
        :hide-button-row="props.hideButtonRow"
        :optionsTeleport="props.optionsTeleport"
        :filter-tags-teleport="props.filterTagsTeleport"
        :small-header="props.smallHeader"
        :user-table-filter-id="`liveTable_${props.configId}`"
        :filename="liveTableOptions.moduleName"
        :row-click-mode="liveTableOptions.moduleType === 'SOSOTradesTable' || liveTableOptions.moduleType === 'EpexMessages' ? 'disabled' : 'default'"
        :no-data-found-message="props.noDataFoundMessage"
        :is-fixed-module="props.isFixedModule"
        :initial-plant-details-tab="props.initialPlantDetailsTab"
        :plant-modal-date-range="plantModalDateRange"
        :cannot-edit="props.cannotEdit"
        prevent-download
        @select:row="selectRow"
        :header="props.hideTitle ? '' : moduleTitle"
    >
        <template v-if="liveTableOptions.showRefreshRealtime" #header-extra>
            <div class="live-table-refresh">
                <span><strong>Table Updated:</strong> {{ tablePulledDate }}</span>
                <action-button
                    title="Refresh table"
                    :icon="['fas', 'arrow-rotate-left']"
                    :is-disabled="loading"
                    @click="getLiveTableData()"
                />
            </div>
        </template>
    </generic-table>
    <epex-contract-information-modal
        v-if="activeEpexContractId"
        v-model:visible="showEpexContractModal"
        :contract-id="activeEpexContractId"
        :country-id="props.countryId"
        @hide="closeEpexContractModal"
    />
</template>

<script setup lang="ts">
import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { ITableFilters, IUserTableFilters } from '@/interfaces/powerMarkets/table/ITableFilters.ts';
import {
    computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useLiveTableStore } from '@/stores/powerMarkets/liveTableStore.ts';
import dayjs from 'dayjs';
import { IComponentTableData } from '@/stores/powerMarkets/IComponentTableData.ts';
import { genericTableDataToCsvRows } from '@/utils/reportExportUtils.ts';
import {
    applyLiveTableTimezoneDisplay,
    applyLiveTableColours,
    ILiveTableDisplaySettings,
    updateTableDataWithPush,
} from '@/helpers/liveTable/liveTableMethods.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { IGenericTableRowData } from '@/interfaces/powerMarkets/table/IGenericTableRowData.ts';
import { useSignalRLifecycle } from '@/realTimeConnection/SignalRLifecycle.ts';
import { useToast } from 'primevue/usetoast';
import { publishToast } from '@/utils/Toast.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useApi, liveTableEndpoints } from '@/api/useApi.ts';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { IDatePeriod } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';
import { ILiveTableOptions } from '@/interfaces/powerMarkets/table/ILiveTableOptions.ts';
import { debounce } from '@/utils/Debounce.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import type { PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';
import { getSelectedPlantGroupColourSettings, getSelectedPlantIds } from '@/helpers/powerMarkets/plantGroupSettings.ts';
import { TimeFormatEnum } from '@/enums/powerMarkets/TimeFormatEnum.ts';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import ActionButton from '@/components/powerMarkets/common/ActionButton.vue';
import EpexContractInformationModal from '@/components/powerMarkets/modals/EpexContractInformationModal.vue';
import { resolveCountryName } from '@/components/powerMarkets/modals/dashboard/AddModuleModal/helpers.ts';
import GenericTable from './GenericTable.vue';

dayjs.extend(advancedFormat);

const toast = useToast();
const liveTableStore = useLiveTableStore();
const uiStore = useUIStore();
const userSettingsStore = useUserSettingsStore();
const dataStore = useDataStore();
const signalRLifecycle = useSignalRLifecycle();

const props = defineProps<{
    actionTypeFilterOverride?: Array<IIdName>;
    configId: string;
    countryId: string;
    extraPullInfo?: string;
    fromDateOverride?: Date;
    toDateOverride?: Date;
    hardcodedTableFilters?: IUserTableFilters | null;
    hideTopFilters?: boolean;
    moduleType: string;
    liveTableDateOverride?: Date;
    optionsTeleport?: HTMLElement | null;
    filterTagsTeleport?: HTMLElement | null;
    hideButtonRow?: boolean;
    smallHeader?: boolean;
    presetFilters?: ITableFilters | null;
    boaPageDateOverride?: IDatePeriod;
    boaModuleDateOverride?: IDatePeriod;
    noDataFoundMessage?: string;
    isFixedModule?: boolean;
    cannotEdit?: boolean;
    hideTitle?: boolean;
    initialPlantDetailsTab?: PlantDetailsTab;
    dateDisplayTimezone?: string | null;
}>();
const emit = defineEmits<{
    'select:row': [IGenericTableRowData];
    'update:can-edit-table': [boolean];
}>();

const localTableData = ref<IGenericTableData | null>(null);
const genericTableRef = ref<InstanceType<typeof GenericTable> | null>(null);
const componentTable = computed<IComponentTableData | null>(() => liveTableStore.componentTables[props.configId]);
const showEpexContractModal = ref<boolean>(false);
const activeEpexContractId = ref<number | null>(null);
const subscribedPushName = ref<string | null>(null);
let isUnmounted = false;
const backendFilters = computed<IUserTableFilters | null>(() => {
    if (props.presetFilters) {
        return {
            headerToSort: {
                ascending: props.presetFilters.ascending,
                id: props.presetFilters.headerToSort,
            },
            currentFiltersByHeading: props.presetFilters.currentFiltersByHeading,
            columnsToHide: props.presetFilters.columnsToHide,
            orderedHeaders: props.presetFilters.orderedHeaders,
            unitLocation: props.presetFilters.unitLocation,
        } as IUserTableFilters;
    }
    return null;
});

const loading = ref<boolean>(false);
const tablePulledDate = ref<string>('');

const canEditTable = computed<boolean>(() => (
    !props.cannotEdit && !!timezoneAdjustedTableData.value?.data?.length
));

const moduleTitle = computed<string>(() => {
    if (props.moduleType === 'Notifications') return liveTableOptions.value?.moduleName ?? 'Notifications';
    const countryName = resolveCountryName(dataStore.countriesById, props.countryId) ?? props.countryId;
    return `${liveTableOptions.value?.moduleName} - ${countryName}`;
});

const timeFormat = computed<string>(() => (
    userSettingsStore.userSettings.otherSettings.data.timeFormatValue === TimeFormatEnum.TwelveHour ? 'h:mm a' : 'H:mm'
));

const liveTableOptions = computed<ILiveTableOptions | null>(() => {
    const foundInfo = Object.values(liveTableStore.liveTableOptions).find(
        (liveTable) => liveTable.moduleType === props.moduleType,
    );
    return foundInfo || null;
});

const effectiveDatePeriod = computed<IDatePeriod>(() => {
    const datePeriod = props.boaPageDateOverride || props.boaModuleDateOverride || dataStore.periodData?.current || {
        date: new Date(),
        period: 1,
    };
    if (liveTableOptions.value?.getOnDayChange && props.liveTableDateOverride) {
        return { ...datePeriod, date: props.liveTableDateOverride };
    }
    return datePeriod;
});

const storeTableKey = computed<string>(() => {
    if (!liveTableOptions.value?.getOnDayChange && !liveTableOptions.value?.getOnPeriodChange) return props.moduleType;

    return liveTableStore.getLiveTableStoreKey(props.moduleType, props.countryId, {
        day: liveTableOptions.value.getOnDayChange ? effectiveDatePeriod.value.date : undefined,
        period: liveTableOptions.value.getOnPeriodChange ? effectiveDatePeriod.value.period : undefined,
    });
});

const liveTableCurrentDate = computed<Date>(() => effectiveDatePeriod.value.date);

const selectedDayRange = computed<IDateRange | undefined>(() => {
    if (liveTableOptions.value?.getOnDayChange) {
        return { from: liveTableCurrentDate.value, to: liveTableCurrentDate.value };
    }
    if (!liveTableOptions.value?.getOnDayRangeChange || !props.fromDateOverride || !props.toDateOverride) return undefined;
    return {
        from: props.fromDateOverride,
        to: props.toDateOverride,
    };
});

const plantModalDateRange = computed<IDateRange>(() => {
    const options = liveTableOptions.value;

    if (selectedDayRange.value) return selectedDayRange.value;

    if (options?.getOnDayChange) {
        const date = new Date(liveTableCurrentDate.value);
        return { from: date, to: date };
    }

    return { from: new Date(), to: new Date() };
});

const liveTableDisplaySettings = computed<ILiveTableDisplaySettings>(() => ({
    bidColour: userSettingsStore.userSettings.otherSettings.data.bidColour,
    bingoNamesToEnums: dataStore.staticStore.bingoNamesToEnums,
    bingoColourOverrides: userSettingsStore.userSettings.otherSettings.data.bingoColourOverrides,
    useDarkHeatmapColours: uiStore.isDarkTheme,
    offerColour: userSettingsStore.userSettings.otherSettings.data.offerColour,
    selectedPlantGroupColourSettings: getSelectedPlantGroupColourSettings(userSettingsStore.userSettings.otherSettings.data),
    selectedPlantIds: getSelectedPlantIds(userSettingsStore.userSettings.otherSettings.data),
}));

const storedTableData = computed<IGenericTableData | null>(() => {
    const countryLower = props.countryId?.toLowerCase() ?? null;
    const tableData = liveTableStore.storeTables[storeTableKey.value];
    if (!tableData || (countryLower && tableData.country?.toLowerCase() !== countryLower)) return null;

    return tableData;
});

const tableDataToUse = computed<IGenericTableData | null>(() => {
    const tableToUse = !liveTableOptions.value?.useStore ? localTableData.value : storedTableData.value;
    return tableToUse ?? null;
});

const filteredTableData = computed<IGenericTableData | null>(() => {
    if (!tableDataToUse.value) return null;
    if (!props.actionTypeFilterOverride) return tableDataToUse.value;
    if (props.actionTypeFilterOverride.length === 0) return tableDataToUse.value;

    const filteredData = { ...tableDataToUse.value };
    const filterNames = props.actionTypeFilterOverride.map((x) => x.name);

    filteredData.data = tableDataToUse.value.data?.filter((row) => {
        const typeValue = row?.row?.Type?.values?.[0];
        if (!typeValue) return false;

        // Return true if ANY filterName is a substring of typeValue e.g 'Turn Down' is a match for Turn Down (notice)
        return filterNames.some((filterName) => typeValue.toString().includes(filterName));
    });

    return filteredData;
});

const timezoneAdjustedTableData = computed<IGenericTableData | null>(() => {
    if (!filteredTableData.value) return null;
    if (!props.dateDisplayTimezone) return filteredTableData.value;
    return applyLiveTableTimezoneDisplay(
        filteredTableData.value,
        props.dateDisplayTimezone,
        timeFormat.value,
    );
});

function applyCurrentTableColours (tableData: IGenericTableData | null): void {
    if (!tableData || !liveTableOptions.value) return;

    applyLiveTableColours(tableData, liveTableOptions.value.moduleType, liveTableDisplaySettings.value);
}

function registerLiveTableForPushes (datePeriod: IDatePeriod, extraPullInfo?: string): void {
    const selectionStoreKey = liveTableOptions.value?.useStore
        && (liveTableOptions.value.getOnDayChange || liveTableOptions.value.getOnPeriodChange)
        ? storeTableKey.value : undefined;
    liveTableStore.addTableOnScreen(
        props.configId,
        props.moduleType,
        props.countryId,
        selectedDayRange.value,
        { ...datePeriod },
        selectionStoreKey,
    );

    const pushName = getSignalRName(props.countryId, datePeriod.date, datePeriod.period, extraPullInfo);
    if (subscribedPushName.value === pushName) return;

    if (subscribedPushName.value) {
        signalRLifecycle.leaveGroup('module', subscribedPushName.value);
    }

    signalRLifecycle.joinGroup('module', pushName);
    subscribedPushName.value = pushName;
}

async function getLiveTableData (retryCount: number = 0): Promise<void> {
    if (isUnmounted) return;
    loading.value = true;

    const userId = userSettingsStore.userSettings.otherSettings.data.userId;
    const datePeriodToUse = effectiveDatePeriod.value;
    const storeKeyToUse = storeTableKey.value;

    const extraParams = getExtraParams(
        props.countryId,
        datePeriodToUse.date,
        datePeriodToUse.period,
        false,
        props.extraPullInfo,
        props.fromDateOverride,
        props.toDateOverride,
    );

    const urlToUse = liveTableEndpoints.find(x => x.liveTableLocation === liveTableOptions.value?.locationByCountry[props.countryId]);

    const apiToUse = useApi(urlToUse?.endpointKey ?? 'main');

    apiToUse.get<IGenericTableData>({ endpoint: `/LiveTable_${props.moduleType}/${userId}${extraParams}` }).then((response) => {
        if (isUnmounted || storeKeyToUse !== storeTableKey.value || response.data === null) return;

        applyCurrentTableColours(response.data);

        if (liveTableOptions.value?.useStore) {
            liveTableStore.addTableToStore(liveTableOptions.value.moduleType, response.data, props.countryId, storeKeyToUse);
        }

        registerLiveTableForPushes(datePeriodToUse, props.extraPullInfo);
        localTableData.value = response.data;
        tablePulledDate.value = dayjs().format('Do MMMM YYYY, h:mm a');
    }).catch(() => {
        if (retryCount >= 3) {
            publishToast(toast, 'Unable to retrieve live table data', 'error');
            return;
        }

        getLiveTableData(retryCount + 1);
    }).finally(() => {
        loading.value = false;
    });
}

function initialiseLiveTable (): void {
    if (liveTableOptions.value?.useStore && storedTableData.value) {
        registerLiveTableForPushes(effectiveDatePeriod.value, props.extraPullInfo);
        loading.value = false;
        return;
    }

    getLiveTableData();
}

function getSignalRName (country: string, date: Date, period: number, extraPullInfo?: string): string {
    const extraParams = getExtraParams(country, date, period, true, extraPullInfo);
    const nameWithExtra = `${props.moduleType}${extraParams}`;
    const name = props.moduleType === 'Notifications'
        ? `Notifications${userSettingsStore.userSettings.otherSettings.data.userId}`
        : nameWithExtra;
    return name;
}

function getExtraParams (country: string, date: Date, period: number, isPush: boolean, extraPullInfo?: string, fromDateRange?: Date, toDateRange?: Date): string {
    let extraParams = '';

    if ((isPush && country.toLowerCase() !== 'gb') || liveTableOptions.value?.requiresCountryString) {
        extraParams += `/${country}`;
    }

    if (liveTableOptions.value?.getOnDayChange) {
        extraParams += `/${dayjs(date).format('YYYY-MM-DD')}`;
    }

    if (liveTableOptions.value?.getOnDayRangeChange && fromDateRange && toDateRange) {
        const fromDate = dayjs(fromDateRange).format('YYYY-MM-DD');
        const toDate = dayjs(toDateRange).format('YYYY-MM-DD');
        extraParams += `/${fromDate}/${toDate}`;
    }
    if (liveTableOptions.value?.getOnPeriodChange) extraParams += `/${period}`;

    if (extraPullInfo) extraParams += `/${extraPullInfo}`;

    return extraParams;
}

function selectRow (row: IGenericTableRowData): void {
    if (liveTableOptions.value?.slideOutOverride === 'epexContract') {
        const contractId = Number(row.uniqueId);
        if (Number.isFinite(contractId)) {
            activeEpexContractId.value = contractId;
            showEpexContractModal.value = true;
            return;
        }
    }

    emit('select:row', row);
}

function closeEpexContractModal (): void {
    activeEpexContractId.value = null;
    showEpexContractModal.value = false;
}

function editTable (event: Event): void {
    genericTableRef.value?.showEditTableOverlay(event);
}

function updateComponentTable (): void {
    if (!tableDataToUse.value || componentTable.value?.reRequest) {
        getLiveTableData();
        return;
    }

    if (!componentTable.value) return;

    const maxPushBatchesPerTick = 10;
    let processed = 0;

    while (componentTable.value.pushes.length > 0 && processed < maxPushBatchesPerTick) {
        const newChange = componentTable.value.pushes.shift();
        if (!newChange) continue;

        const updateResponse = updateTableDataWithPush(tableDataToUse.value, newChange, liveTableOptions.value?.maxEntriesOnTable ?? null);
        if (!updateResponse.success) {
            toast.add({
                summary: 'Error in table update',
                severity: 'warn',
                detail: updateResponse.error,
                life: 10000,
            });
            getLiveTableData();
            componentTable.value.pushes = [];
            return;
        }

        processed += 1;
    }

    if (!liveTableOptions.value?.useStore) {
        localTableData.value = tableDataToUse.value;
    }

    if (componentTable.value.pushes.length > 0) {
        window.requestAnimationFrame(updateComponentTable);
    }
}

const updateComponentTableDebounced = debounce(updateComponentTable);

watch(canEditTable, (value) => {
    emit('update:can-edit-table', value);
}, { immediate: true });

watch(() => componentTable.value?.pushes.length, (len) => {
    if (!len) return;
    updateComponentTableDebounced();
});

watch(() => tableDataToUse.value?.reRequestCounter, (newVal, oldVal) => {
    if (!newVal || newVal === oldVal) return;
    getLiveTableData();
});

watch(
    () => [
        uiStore.theme,
        dataStore.staticStore.bingoNamesToEnums,
        userSettingsStore.userSettings.otherSettings.data.bidColour,
        userSettingsStore.userSettings.otherSettings.data.offerColour,
        userSettingsStore.userSettings.otherSettings.data.bingoColourOverrides,
        userSettingsStore.userSettings.otherSettings.data.chosenPlantGroupName,
        userSettingsStore.userSettings.otherSettings.data.chosenPlantsByGroup,
        userSettingsStore.userSettings.otherSettings.data.defaultPlantGroupColour,
        userSettingsStore.userSettings.otherSettings.data.plantGroupColourOverrideByGroup,
    ],
    () => {
        if (!tableDataToUse.value) return;
        applyCurrentTableColours(tableDataToUse.value);
    },
    { deep: true },
);

watch(
    (): string | undefined => props.extraPullInfo,
    () => {
        getLiveTableData();
    },
);

watch(
    [
        (): Date | undefined => props.fromDateOverride,
        (): Date | undefined => props.toDateOverride,
    ],
    ([fromDate, toDate]) => {
        if (!liveTableOptions.value?.getOnDayRangeChange || !fromDate || !toDate) return;
        getLiveTableData();
    },
);

watch(
    [
        (): string | undefined => (liveTableOptions.value?.getOnDayChange ? dayjs(effectiveDatePeriod.value.date).format('YYYY-MM-DD') : undefined),
        (): number | undefined => (liveTableOptions.value?.getOnPeriodChange ? effectiveDatePeriod.value.period : undefined),
    ],
    () => {
        initialiseLiveTable();
    },
);

function getReportCsvRows (): Array<Array<string | number | null | undefined>> {
    if (!timezoneAdjustedTableData.value) return [];
    return genericTableDataToCsvRows(timezoneAdjustedTableData.value);
}

defineExpose({
    getReportCsvRows,
    editTable,
});

onMounted(initialiseLiveTable);

onBeforeUnmount(() => {
    isUnmounted = true;
    liveTableStore.removeTableOnScreen(props.configId);
});

</script>

<style scoped lang="scss">
.live-table-refresh {
    display: inline-flex;
    align-items: center;
    gap: var(--extraSmallSpacing);
    font-size: var(--smallFontSize);
    margin-left: var(--defaultSpacing);
}
</style>
